import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import NewsCommentForm from './NewsCommentForm';

const formatDateTime = (createdAt) => {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${hh}:${mm}`;
};

const styles = {
  writerName: {
    variant: 'subtitle2',
    sx: { fontWeight: 700, fontSize: 13, mb: 0.3 },
  },
  commentContent: {
    variant: 'body2',
    sx: { fontSize: 13, whiteSpace: 'pre-line' },
  },
  metaBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: 'text.secondary',
    fontSize: 11,
  },
  metaText: {
    variant: 'caption',
    sx: { fontSize: 11 },
  },
  actionText: {
    variant: 'caption',
    sx: { fontSize: 11, cursor: 'pointer' },
  },
  childContainer: {
    mt: 1,
    pl: 2,
    pt: 1,
    borderTop: '1px solid #f0f0f0',
  },
  childBox: {
    mb: 1,
    pb: 1,
  },
  parentBox: {
    pb: 1.5,
    borderBottom: '1px solid #f0f0f0',
  },
};

const getWriterLabel = (c) => c?.writer_name || '익명';

const toTime = (createdAt) =>
  createdAt ? Date.parse(createdAt) : Number.POSITIVE_INFINITY;

const NewsCommentList = ({
  comments = [],
  currentUser,
  submitting = false,
  onSubmitReply,
  onUpdate,
  onDelete,
  onToggleLike,
}) => {
  const [replyAnchor, setReplyAnchor] = useState(null);
  const [editAnchor, setEditAnchor] = useState(null);

  if (!comments.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        아직 댓글이 없습니다.
      </Typography>
    );
  }

  const isAdmin = currentUser?.role === 'ADMIN';
  const currentUserId = currentUser?.id ?? currentUser?.userId ?? currentUser?.user_id;
  const canReply = Boolean(onSubmitReply && currentUser);
  const canLike = Boolean(onToggleLike);

  const openReply = ({ anchorCommentId, parentCommentId }) => {
    setReplyAnchor((prev) => {
      if (prev?.anchorCommentId === anchorCommentId) return null;
      return { anchorCommentId, parentCommentId };
    });
  };

  const closeReply = () => setReplyAnchor(null);

  const openEdit = (comment) => {
    // display_name이 있으면 그것을 사용, 없으면 user_name(관리자가 처음 설정한 이름) 사용
    const initialDisplayName = comment.display_name !== null && comment.display_name !== undefined
      ? comment.display_name
      : (comment.user_name || null);
    setEditAnchor({ 
      commentId: comment.comment_id, 
      initialContent: comment.content,
      initialDisplayName,
    });
    setReplyAnchor(null);
  };

  const closeEdit = () => setEditAnchor(null);

  const commentById = new Map(comments.map((c) => [c.comment_id, c]));
  const renderLikeButton = (comment) => {
    if (!canLike || !comment?.comment_id) return null;
    const liked = Boolean(comment.liked_by_viewer);
    const count = comment.like_count ?? 0;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          width: 40,
          height: 40,
        }}
        onClick={() => onToggleLike && onToggleLike(comment, !liked)}
      >
        {liked ? (
          <FavoriteIcon sx={{ fontSize: 18, color: '#f0435c' }} />
        ) : (
          <FavoriteBorderIcon sx={{ fontSize: 18, color: '#d1d5db' }} />
        )}
        <Typography variant="caption" sx={{ fontSize: 11, color: '#6b7280', mt: 0.3 }}>
          {count}
        </Typography>
      </Box>
    );
  };

  const renderLikeOverlay = (comment) => {
    const button = renderLikeButton(comment);
    if (!button) return null;
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
        }}
      >
        {button}
      </Box>
    );
  };

  const getBestBadgeStyle = (count) => {
    if (count >= 20) return { color: '#fff', bg: '#f87171' };
    if (count >= 10) return { color: '#fff', bg: '#fb923c' };
    if (count >= 5) return { color: '#fff', bg: '#4d8eff' };
    return null;
  };

  const renderBestLabel = (comment, { allowBadge = true } = {}) => {
    if (!allowBadge) return null;
    const count = comment?.like_count ?? 0;
    const badgeStyle = getBestBadgeStyle(count);
    if (!badgeStyle) return null;
    return (
      <Box
        sx={{
          px: 0.9,
          py: 0.1,
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 700,
          color: badgeStyle.color,
          bgcolor: badgeStyle.bg,
        }}
      >
        BEST
      </Box>
    );
  };

  const getReplyToLabel = ({ child, fallbackLabel = null }) => {
    if (!child?.reply_to_comment_id) return fallbackLabel;
    const target = commentById.get(child.reply_to_comment_id) || null;
    return target ? getWriterLabel(target) : fallbackLabel;
  };

  const renderReplyLink = ({ anchorCommentId, parentCommentId }) => {
    if (!canReply) return null;

    return (
      <Typography
        variant={styles.actionText.variant}
        sx={styles.actionText.sx}
        onClick={() => openReply({ anchorCommentId, parentCommentId })}
      >
        답글쓰기
      </Typography>
    );
  };

  const renderReplyForm = () => {
    if (!replyAnchor) return null;

    return (
      <Box sx={{ mt: 1 }}>
        <NewsCommentForm
          key={`reply-${replyAnchor.anchorCommentId}`}
          currentUser={currentUser}
          submitting={submitting}
          onSubmit={async (text, displayName) => {
            if (!onSubmitReply) return;
            await onSubmitReply({
              parentCommentId: replyAnchor.parentCommentId,
              replyToCommentId: replyAnchor.anchorCommentId,
              text,
              displayName,
            });
            closeReply();
          }}
          onCancel={closeReply}
        />
      </Box>
    );
  };

  const parents = comments.filter((c) => !c.parent_comment_id);
  const childrenMap = comments.reduce((acc, c) => {
    if (!c.parent_comment_id) return acc;
    if (!acc[c.parent_comment_id]) acc[c.parent_comment_id] = [];
    acc[c.parent_comment_id].push(c);
    return acc;
  }, {});

  const parentIdSet = new Set(parents.map((p) => p.comment_id));
  const orphanParentIds = Object.keys(childrenMap)
    .map((id) => Number(id))
    .filter((id) => id && !parentIdSet.has(id));

  const topLevel = [
    ...parents,
    ...orphanParentIds.map((id) => {
      const childList = childrenMap[id] || [];
      return {
        comment_id: id,
        isDeletedParent: true,
        created_at: childList[0]?.created_at,
      };
    }),
  ].sort((a, b) => {
    const aTime = toTime(a?.created_at);
    const bTime = toTime(b?.created_at);
    if (aTime !== bTime) return aTime - bTime;
    return (a.comment_id || 0) - (b.comment_id || 0);
  });

  return (
    <Box sx={{ mt: 1 }}>
      {topLevel.map((comment, index) => {
        const childList = childrenMap[comment.comment_id] || [];

        if (comment.isDeletedParent) {
          return (
            <Box
              key={`deleted-${comment.comment_id}`}
              sx={{
                pt: index === 0 ? 0 : 1.5,
                ...styles.parentBox,
              }}
            >
              <Typography
                variant={styles.writerName.variant}
                sx={{ ...styles.writerName.sx, color: 'text.secondary' }}
              >
                삭제된 댓글입니다.
              </Typography>

              {childList.length > 0 && (
                <Box sx={styles.childContainer}>
                  {childList.map((child, childIndex) => {
                    const childWriterLabel = getWriterLabel(child);
                    const childCanDelete = isAdmin || currentUserId === child.writer_id;
                    const isLastChild = childIndex === childList.length - 1;
                    const replyToLabel = getReplyToLabel({ child, fallbackLabel: null });

                    return (
                      <Box
                        key={child.comment_id}
                        sx={{
                          ...styles.childBox,
                          mb: isLastChild ? 0 : 1,
                          borderBottom: isLastChild ? 'none' : '1px solid #f0f0f0',
                        }}
                      >
                        <Box sx={{ position: 'relative', pr: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                            <Typography variant={styles.writerName.variant} sx={styles.writerName.sx}>
                              {childWriterLabel}
                            </Typography>
                            {renderBestLabel(child, { allowBadge: false })}
                          </Box>
                          <Typography variant={styles.commentContent.variant} sx={styles.commentContent.sx}>
                            {replyToLabel ? (
                              <Box component="span" sx={{ fontWeight: 700, mr: 0.5, display: 'inline' }}>
                                {replyToLabel}
                              </Box>
                            ) : null}
                            {child.content}
                          </Typography>
                          <Box sx={{ ...styles.metaBox, mt: 0.4 }}>
                            <Typography variant={styles.metaText.variant} sx={styles.metaText.sx}>
                              {formatDateTime(child.created_at)}
                            </Typography>
                            {renderReplyLink({
                              anchorCommentId: child.comment_id,
                              parentCommentId: comment.comment_id,
                            })}
                            {childCanDelete && (
                              <Typography
                                variant={styles.actionText.variant}
                                sx={styles.actionText.sx}
                                onClick={() => onDelete && onDelete(child)}
                              >
                                삭제
                              </Typography>
                            )}
                          </Box>
                          {renderLikeOverlay(child)}
                        </Box>

                        {replyAnchor?.anchorCommentId === child.comment_id ? renderReplyForm() : null}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        }

        const canDelete = isAdmin || currentUserId === comment.writer_id;
        const canEdit = currentUserId === comment.writer_id;
        const writerLabel = getWriterLabel(comment);
        const isEditing = editAnchor?.commentId === comment.comment_id;

        return (
          <Box
            key={comment.comment_id}
            sx={{
              pt: index === 0 ? 0 : 1.5,
              ...styles.parentBox,
            }}
          >
            {isEditing ? (
              <NewsCommentForm
                currentUser={currentUser}
                submitting={submitting}
                initialValue={editAnchor.initialContent}
                initialDisplayName={editAnchor.initialDisplayName}
                onSubmit={async (text, displayName) => {
                  if (onUpdate) {
                    await onUpdate(comment.comment_id, text, displayName);
                  }
                  closeEdit();
                }}
                onCancel={closeEdit}
              />
            ) : (
              <>
                <Box sx={{ position: 'relative', pr: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                    <Typography variant={styles.writerName.variant} sx={styles.writerName.sx}>
                      {writerLabel}
                    </Typography>
                    {renderBestLabel(comment)}
                  </Box>
                  <Typography variant={styles.commentContent.variant} sx={styles.commentContent.sx}>
                    {comment.content}
                  </Typography>
                  <Box sx={{ ...styles.metaBox, mt: 0.5 }}>
                    <Typography variant={styles.metaText.variant} sx={styles.metaText.sx}>
                      {formatDateTime(comment.created_at)}
                    </Typography>
                    {renderReplyLink({
                      anchorCommentId: comment.comment_id,
                      parentCommentId: comment.comment_id,
                    })}
                    {canEdit && (
                      <Typography
                        variant={styles.actionText.variant}
                        sx={styles.actionText.sx}
                        onClick={() => openEdit(comment)}
                      >
                        수정
                      </Typography>
                    )}
                    {canDelete && (
                      <Typography
                        variant={styles.actionText.variant}
                        sx={styles.actionText.sx}
                        onClick={() => onDelete && onDelete(comment)}
                      >
                        삭제
                      </Typography>
                    )}
                  </Box>
                  {renderLikeOverlay(comment)}
                </Box>
              </>
            )}

            {replyAnchor?.anchorCommentId === comment.comment_id ? renderReplyForm() : null}

            {childList.length > 0 && (
              <Box sx={styles.childContainer}>
                {childList.map((child, childIndex) => {
                  const childWriterLabel = getWriterLabel(child);
                  const childCanDelete = isAdmin || currentUserId === child.writer_id;
                  const childCanEdit = currentUserId === child.writer_id;
                  const isLastChild = childIndex === childList.length - 1;
                  const replyToLabel = getReplyToLabel({ child, fallbackLabel: writerLabel });
                  const isEditing = editAnchor?.commentId === child.comment_id;

                  return (
                    <Box
                      key={child.comment_id}
                      sx={{
                        ...styles.childBox,
                        mb: isLastChild ? 0 : 1,
                        borderBottom: isLastChild ? 'none' : '1px solid #f0f0f0',
                      }}
                    >
                      {isEditing ? (
                        <NewsCommentForm
                          currentUser={currentUser}
                          submitting={submitting}
                          initialValue={editAnchor.initialContent}
                          initialDisplayName={editAnchor.initialDisplayName}
                          onSubmit={async (text, displayName) => {
                            if (onUpdate) {
                              await onUpdate(child.comment_id, text, displayName);
                            }
                            closeEdit();
                          }}
                          onCancel={closeEdit}
                        />
                      ) : (
                        <Box sx={{ position: 'relative', pr: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                            <Typography variant={styles.writerName.variant} sx={styles.writerName.sx}>
                              {childWriterLabel}
                            </Typography>
                            {renderBestLabel(child, { allowBadge: false })}
                          </Box>
                          <Typography variant={styles.commentContent.variant} sx={styles.commentContent.sx}>
                            <Box component="span" sx={{ fontWeight: 700, mr: 0.5, display: 'inline' }}>
                              {replyToLabel}
                            </Box>
                            {child.content}
                          </Typography>
                          <Box sx={{ ...styles.metaBox, mt: 0.4 }}>
                            <Typography variant={styles.metaText.variant} sx={styles.metaText.sx}>
                              {formatDateTime(child.created_at)}
                            </Typography>
                            {renderReplyLink({
                              anchorCommentId: child.comment_id,
                              parentCommentId: comment.comment_id,
                            })}
                            {childCanEdit && (
                              <Typography
                                variant={styles.actionText.variant}
                                sx={styles.actionText.sx}
                                onClick={() => openEdit(child)}
                              >
                                수정
                              </Typography>
                            )}
                            {childCanDelete && (
                              <Typography
                                variant={styles.actionText.variant}
                                sx={styles.actionText.sx}
                                onClick={() => onDelete && onDelete(child)}
                              >
                                삭제
                              </Typography>
                            )}
                          </Box>
                          {renderLikeOverlay(child)}
                        </Box>
                      )}

                      {replyAnchor?.anchorCommentId === child.comment_id ? renderReplyForm() : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default NewsCommentList;

